import React, { createContext, useContext, useRef, useState, useCallback } from 'react';

const BedDataContext = createContext(null);

export const BedDataProvider = ({ children }) => {
  const bedDataRef = useRef({});
  const [bedDataSnapshot, setBedDataSnapshot] = useState({});

  const dataQueueRef = useRef({});
  const dataQueueListenersRef = useRef({});

  // 🔥 chartIndex — ref로 관리 (Recoil 제거)
  // key: serialKey → value: number
  const chartIndexRef = useRef({});

  // 🔥 chartInit — ref로 관리 (Recoil 제거)
  // key: serialKey → value: { isGlobalInit, initStartTime, readyComponents: Set, totalComponents }
  const chartInitRef = useRef({});
  // chartInit 변경 시 구독 알림용
  const chartInitListenersRef = useRef({});

  const getBedData = useCallback(() => {
    return bedDataRef.current;
  }, []);

  const getSerialData = useCallback((serial) => {
    return bedDataRef.current[serial] || null;
  }, []);

  const updateBedData = useCallback((updater) => {
    if (typeof updater === 'function') {
      bedDataRef.current = updater(bedDataRef.current);
    } else {
      bedDataRef.current = updater;
    }
  }, []);

  const updateWaveData = useCallback((serial, waveData) => {
    if (!bedDataRef.current[serial]) {
      bedDataRef.current[serial] = {};
    }
    bedDataRef.current[serial].waveData = waveData;
  }, []);

  const consumeWaveData = useCallback((serial, type) => {
    const serialData = bedDataRef.current[serial];
    if (serialData?.waveData?.[type]) {
      const data = serialData.waveData[type];
      delete serialData.waveData[type];
      return data;
    }
    return null;
  }, []);

  const createSnapshot = useCallback(() => {
    const ref = bedDataRef.current;
    const snapshot = {};
    const serials = Object.keys(ref);
    for (let i = 0; i < serials.length; i++) {
      const serial = serials[i];
      const data = ref[serial];
      if (!data) continue;
      snapshot[serial] = {
        ...data,
        waveData: data.waveData ? { ...data.waveData } : {}
      };
    }
    setBedDataSnapshot(snapshot);
  }, []);

  // === dataQueue ===

  const getQueueKey = useCallback((serial, type) => `${serial}_${type}`, []);

  const getDataQueue = useCallback((serial, type) => {
    const key = getQueueKey(serial, type);
    if (!dataQueueRef.current[key]) {
      dataQueueRef.current[key] = { data: [] };
    }
    return dataQueueRef.current[key];
  }, []);

  const pushToDataQueue = useCallback((serial, type, newData) => {
    const key = getQueueKey(serial, type);
    if (!dataQueueRef.current[key]) {
      dataQueueRef.current[key] = { data: [] };
    }
    dataQueueRef.current[key].data.push(newData);
    const listener = dataQueueListenersRef.current[key];
    if (listener) {
      listener(dataQueueRef.current[key].data.length);
    }
  }, []);

  const sliceDataQueue = useCallback((serial, type, fromIndex) => {
    const key = getQueueKey(serial, type);
    if (!dataQueueRef.current[key]) return;
    dataQueueRef.current[key].data = dataQueueRef.current[key].data.slice(fromIndex);
  }, []);

  const trimDataQueue = useCallback((serial, type, maxCount, keepCount) => {
    const key = getQueueKey(serial, type);
    if (!dataQueueRef.current[key]) return;
    const queue = dataQueueRef.current[key].data;
    if (queue.length > maxCount) {
      dataQueueRef.current[key].data = queue.slice(queue.length - keepCount);
    }
  }, []);

  const clearDataQueue = useCallback((serial, type) => {
    const key = getQueueKey(serial, type);
    if (dataQueueRef.current[key]) {
      dataQueueRef.current[key].data = [];
    }
  }, []);

  const getDataQueueLength = useCallback((serial, type) => {
    const key = getQueueKey(serial, type);
    return dataQueueRef.current[key]?.data?.length || 0;
  }, []);

  const subscribeDataQueue = useCallback((serial, type, listener) => {
    const key = getQueueKey(serial, type);
    dataQueueListenersRef.current[key] = listener;
    return () => {
      delete dataQueueListenersRef.current[key];
    };
  }, []);

  // === chartIndex (ref) ===

  const getChartIndex = useCallback((serialKey) => {
    return chartIndexRef.current[serialKey] || 0;
  }, []);

  const setChartIndex = useCallback((serialKey, value) => {
    chartIndexRef.current[serialKey] = value;
  }, []);

  // === chartInit (ref + 리스너) ===

  const getChartInit = useCallback((serialKey) => {
    if (!chartInitRef.current[serialKey]) {
      chartInitRef.current[serialKey] = {
        isGlobalInit: false,
        initStartTime: null,
        readyComponents: new Set(),
        totalComponents: 0
      };
    }
    return chartInitRef.current[serialKey];
  }, []);

  const setChartInit = useCallback((serialKey, updater) => {
    if (!chartInitRef.current[serialKey]) {
      chartInitRef.current[serialKey] = {
        isGlobalInit: false,
        initStartTime: null,
        readyComponents: new Set(),
        totalComponents: 0
      };
    }
    if (typeof updater === 'function') {
      chartInitRef.current[serialKey] = updater(chartInitRef.current[serialKey]);
    } else {
      chartInitRef.current[serialKey] = updater;
    }
    // 리스너 알림
    const listener = chartInitListenersRef.current[serialKey];
    if (listener) {
      listener(chartInitRef.current[serialKey]);
    }
  }, []);

  const subscribeChartInit = useCallback((serialKey, listener) => {
    chartInitListenersRef.current[serialKey] = listener;
    return () => {
      delete chartInitListenersRef.current[serialKey];
    };
  }, []);

  return (
    <BedDataContext.Provider value={{
      bedDataRef,
      bedDataSnapshot,
      getBedData,
      getSerialData,
      updateBedData,
      updateWaveData,
      consumeWaveData,
      createSnapshot,
      dataQueueRef,
      getDataQueue,
      pushToDataQueue,
      sliceDataQueue,
      trimDataQueue,
      clearDataQueue,
      getDataQueueLength,
      subscribeDataQueue,
      // 🔥 chartIndex
      getChartIndex,
      setChartIndex,
      // 🔥 chartInit
      getChartInit,
      setChartInit,
      subscribeChartInit
    }}>
      {children}
    </BedDataContext.Provider>
  );
};

export const useBedData = () => {
  const ctx = useContext(BedDataContext);
  if (!ctx) throw new Error('useBedData must be used within BedDataProvider');
  return ctx;
};

export default BedDataContext;